import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { Play, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const StartTimerModal = ({ open, onClose, onStart, projects = [] }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [description, setDescription] = useState('');
  const [prediction, setPrediction] = useState(null);
  const [showPrediction, setShowPrediction] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchPrediction();
      setDescription('');
      setShowPrediction(true);
    }
  }, [open]);

  const fetchPrediction = async () => {
    try {
      const response = await axios.post(`${API}/projects/predict`, {
        date: new Date().toISOString(),
        time: new Date().toISOString()
      });
      
      if (response.data) {
        setPrediction(response.data);
        // Auto-select if high confidence
        if (response.data.confidence >= 0.75 && !selectedProject) {
          setSelectedProject(response.data.project_id);
        }
      }
    } catch (error) {
      console.log('No prediction available');
    }
  };

  const handleStart = async () => {
    if (!selectedProject) {
      toast.error('Please select a project');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/timer/start`, {
        project_id: selectedProject,
        description: description
      });

      if (response.data.error) {
        toast.error(response.data.error);
      } else {
        toast.success('Timer started!');
        if (onStart) onStart();
        onClose();
      }
    } catch (error) {
      console.error('Failed to start timer:', error);
      toast.error(error.response?.data?.detail || 'Failed to start timer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      {/* Container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        {/* Modal Panel */}
        <Dialog.Panel className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-[95vw] md:max-w-[500px] max-h-[85vh] md:max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-200 dark:border-gray-700 transition-colors" data-testid="start-timer-modal">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800 z-10 transition-colors">
            <div>
              <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors">Start Timer</Dialog.Title>
              <Dialog.Description className="text-sm text-gray-600 dark:text-gray-400 mt-1 transition-colors">
                Choose a project and start tracking your time
              </Dialog.Description>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 min-w-[44px] min-h-[44px] flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-4 bg-white dark:bg-gray-800 transition-colors">
            {/* AI Prediction Banner */}
            {prediction && showPrediction && (
              <div className={`rounded-lg p-4 border-2 transition-colors ${
                prediction.confidence >= 0.8 ? 'bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-600' :
                prediction.confidence >= 0.6 ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-500 dark:border-yellow-600' :
                'bg-gray-50 dark:bg-gray-700/50 border-gray-400 dark:border-gray-600'
            }`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-500 dark:text-purple-400 transition-colors" />
                  <div>
                    <h3 className="font-semibold text-sm text-gray-800 dark:text-gray-200 transition-colors">
                      AI Suggestion
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400 transition-colors">{prediction.reason}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowPrediction(false)}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 text-sm transition-colors"
                >
                  ✕
                </button>
              </div>
              
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold transition-colors" style={{ color: prediction.project_color }}>
                  {prediction.project_name}
                </span>
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400 transition-colors">
                  {Math.round(prediction.confidence * 100)}% confident
                </span>
              </div>
              
              {selectedProject !== prediction.project_id && (
                <button
                  onClick={() => setSelectedProject(prediction.project_id)}
                  className="w-full py-2 px-4 bg-purple-500 dark:bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-600 dark:hover:bg-purple-700 transition-all"
                >
                  Use This Project
                </button>
              )}
            </div>
            )}

          {/* Project Selection */}
          <div>
            <label htmlFor="project" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">Project *</label>
            <select
              id="project"
              value={selectedProject || ''}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="flex h-11 w-full rounded-md border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:focus-visible:ring-cyan-400 focus-visible:border-cyan-500 dark:focus-visible:border-cyan-400 focus-visible:ring-offset-2 mt-1 transition-colors font-medium"
              data-testid="project-select"
            >
              <option value="">Select project...</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                  {prediction && project.id === prediction.project_id && 
                    ` (AI: ${Math.round(prediction.confidence * 100)}%)`
                  }
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">What are you working on? (optional)</label>
            <input
              id="description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Building login feature"
              className="mt-1 w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:focus-visible:ring-cyan-400 focus-visible:border-cyan-500 dark:focus-visible:border-cyan-400 transition-colors placeholder:text-gray-500 dark:placeholder:text-gray-400 font-medium"
              data-testid="description-input"
            />
          </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transition-colors">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium disabled:opacity-50 min-h-[44px]"
            >
              Cancel
            </button>
            <button
              onClick={handleStart}
              disabled={!selectedProject || loading}
              className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-4 py-2 rounded-lg disabled:opacity-50 min-h-[44px] flex items-center justify-center transition-all font-medium"
              data-testid="start-timer-button"
            >
              <Play className="w-4 h-4 mr-2" />
              {loading ? 'Starting...' : 'Start Timer'}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default StartTimerModal;
